import {
  type AppInventoryState,
  AppInventoryVersion,
  type AppInventoryVersionType,
} from '@openx/types/targeting/appInventory';
import {
  type AppInventoryType,
  type InventoryAndContentState,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import type { TargetingFieldsValues } from '@openx/types/targeting/targeting';
import type { ComparisonMap } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import type { Dispatch, SetStateAction } from 'react';
import type { AnyAction } from 'redux';
import { AppInventoryV2FormPreviewItems } from '../../../AppInventory/components/AppInventoryFormPreviewItems/AppInventoryV2FormPreviewItems';
import { AppInventoryItemsSection } from '../../../InventoryAndContent/InventoryAndContentItems/AppInventoryItemsSection';
import {
  deriveComparisonType,
  isAppBundleIdPayload,
  isAppInventoryPayload,
} from '../../../InventoryAndContent/InventoryAndContentItems/utils';

type AppInventoryFormPreviewSectionRendererProps = {
  field?: TargetingFieldsValues;
  targetingParams: AppInventoryState | InventoryAndContentState;
  dispatchInventory: (action: AnyAction) => void;
  switchState: boolean | undefined;
  accountUid: string;
  inventoryListItems: string[] | undefined;
  setInventoryListItems: Dispatch<SetStateAction<string[] | undefined>>;
  inventoryVersion: AppInventoryVersionType | undefined;
  isDisabled?: boolean;
  onOpenDrawerClick: (option: AppInventoryType) => void;
  onRemoveAppName: () => void;
  onRemoveBundleIdManual: () => void;
  onRemoveBundleIdInventoryList: () => void;
};

export const appInventoryFormPreviewSectionRenderer = ({
  field,
  targetingParams,
  dispatchInventory,
  switchState,
  accountUid,
  inventoryListItems,
  setInventoryListItems,
  inventoryVersion,
  onOpenDrawerClick,
  onRemoveAppName,
  onRemoveBundleIdManual,
  onRemoveBundleIdInventoryList,
}: AppInventoryFormPreviewSectionRendererProps) => {
  const appGroupPayload = targetingParams[InventoryContentOption.APP_GROUP];
  const appBundleIdPayload = targetingParams[InventoryContentOption.APP_BUNDLE_ID];

  if (!isAppInventoryPayload(appGroupPayload) || !isAppBundleIdPayload(appBundleIdPayload)) {
    return null;
  }

  if (inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2)
    return (
      <AppInventoryV2FormPreviewItems
        field={field}
        appInventoryContent={appGroupPayload}
        appBundleIdContent={appBundleIdPayload}
        readonly={true}
        bundleIdComparisonType={deriveComparisonType(appBundleIdPayload?.val as ComparisonMap | undefined)}
        switchState={switchState}
        accountUid={accountUid}
        inventoryListItems={inventoryListItems}
        setInventoryListItems={setInventoryListItems}
        inventoryVersion={inventoryVersion}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemoveAppName={onRemoveAppName}
        onRemoveBundleIdManual={onRemoveBundleIdManual}
        onRemoveBundleIdInventoryList={onRemoveBundleIdInventoryList}
      />
    );

  return (
    <AppInventoryItemsSection
      field={field}
      appInventoryContent={appGroupPayload}
      appBundleIdContent={appBundleIdPayload}
      dispatchInventory={dispatchInventory}
      readonly={true}
      appNameComparisonType={deriveComparisonType(appGroupPayload?.val as ComparisonMap | undefined)}
      setAppNameComparisonType={noop}
      bundleIdComparisonType={deriveComparisonType(appBundleIdPayload?.val as ComparisonMap | undefined)}
      setBundleIdComparisonType={noop}
      isNotLastItem={false}
      switchState={switchState}
      showDimensionChipLine={false}
      accountUid={accountUid}
      inventoryListItems={inventoryListItems}
      setInventoryListItems={setInventoryListItems}
      inventoryVersion={inventoryVersion}
    />
  );
};
